import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Navbar } from '@/components/Navbar'
import { CommentItem } from '@/components/CommentItem'
import { ReplyModal } from '@/components/ReplyModal'
import apiService from '@/api/service'
import type { CommentDetail } from '@/types/api'

export const Details = (): JSX.Element => {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const [comments, setComments] = useState<CommentDetail[]>([])
    const [loading, setLoading] = useState(false)
    const [isReplyModalOpen, setIsReplyModalOpen] = useState(false)
    const [selectedComment, setSelectedComment] = useState<CommentDetail | null>(null)
    const [averageRating, setAverageRating] = useState<number>(0)
    const [totalComments, setTotalComments] = useState<number>(0)
    const [error, setError] = useState<string>('')

    // 從 URL 參數取得分類或關鍵字資訊
    const name = searchParams.get('name') || '食材新鮮度'
    const type = (searchParams.get('type') as 'category' | 'keywords') || 'category'
    const sentiment = (searchParams.get('sentiment') as 'positive' | 'negative') || 'positive'
    const storeCode = searchParams.get('store_code') || ''

    useEffect(() => {
        const fetchComments = async () => {
            try {
                setLoading(true)
                setError('')

                // 檢查必要參數
                if (!storeCode) {
                    console.error('缺少 store_code 參數')
                    setError('缺少必要參數')
                    return
                }

                console.log('正在獲取評論資料...', { name, type, sentiment, storeCode })

                // 調用 queryType API
                const response = await apiService.queryType({
                    store_code: storeCode,
                    name: name,
                    type: type,
                    sentiment: sentiment
                })

                console.log('評論資料獲取成功:', response)

                // 檢查是否為 token 失效錯誤
                if (response && response.status === 'failed' && 
                    response.message && response.message.includes('This tokens is invalid, please login again')) {
                    console.warn('Token 已失效，清除登入資料並跳轉到登入頁面')
                    
                    // 清除所有登入相關資料
                    apiService.logout()
                    
                    // 跳轉到登入頁面
                    navigate('/login')
                    return
                }

                // 更新狀態
                setComments(response.comments)
                setAverageRating(response.averageRating)
                setTotalComments(response.commentsTotal)

            } catch (error) {
                console.error('Failed to fetch comments:', error)
                const errorMessage = error instanceof Error ? error.message : '載入資料失敗'
                setError(errorMessage)
            } finally {
                setLoading(false)
            }
        }

        fetchComments()
    }, [name, type, sentiment, storeCode, navigate])

    // 當選擇新的評論時，重置 Modal 狀態
    const handleCommentSelect = (comment: CommentDetail) => {
        console.log('選擇新評論，重置 Modal 狀態')
        setSelectedComment(comment)
        setIsReplyModalOpen(true)
    }

    // 關閉彈窗
    const handleCloseModal = () => {
        setIsReplyModalOpen(false)
        setSelectedComment(null)
    }

    const handleBack = () => {
        navigate('/dashboard')
    }

    return (
        <>
            {/* 回覆彈窗 */}
            <ReplyModal
                isOpen={isReplyModalOpen}
                onClose={handleCloseModal}
                comment={selectedComment}
            />

            {/* 主內容 */}
            <div className="flex flex-col min-h-screen items-start relative bg-[#e8d080]">
                <Navbar />

                <div className="flex flex-col flex-1 items-start relative self-stretch w-full bg-[#e8d080] overflow-y-auto">
                    {/* 標題區域 - 响应式 */}
                    <div className="flex flex-col items-center gap-3 md:gap-[17px] relative self-stretch w-full bg-white overflow-hidden py-6 md:py-8">
                        {/* 返回按鈕 - 响应式 */}
                        <button
                            onClick={handleBack}
                            className="absolute top-3 left-3 md:top-4 md:left-4 w-[50px] h-[50px] md:w-[60px] md:h-[60px] flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors"
                        >
                            <div className="relative w-8 h-8 md:w-10 md:h-10">
                                <img
                                    className="absolute w-[25.00%] h-[50.00%] top-[25.00%] left-[25.00%]"
                                    alt="Vector"
                                    src="/img/vector-10.svg"
                                />
                                <img
                                    className="absolute w-[25.00%] h-[50.00%] top-[25.00%] left-[50.00%]"
                                    alt="Vector"
                                    src="/img/vector-10.svg"
                                />
                            </div>
                        </button>

                        {/* 分類/關鍵字名稱 - 响应式 */}
                        <div className="w-full flex items-center justify-center [font-family:'Noto_Sans_TC',Helvetica] font-bold text-[#929084] text-[50px] text-center tracking-[12.80px] leading-[normal] px-16">
                            {name}
                        </div>
                    </div>

                    {/* 資料統計標題 - 响应式 */}
                    <div className="relative flex items-center justify-center self-stretch h-16 md:h-20 [font-family:'Noto_Sans_TC',Helvetica] font-bold text-white text-[32px] text-center tracking-[10.00px] leading-tight md:leading-[35.8px]">
                        資料統計
                    </div>

                    {/* 統計摘要 - 响应式 */}
                    <div className="relative self-stretch w-full flex items-center justify-center py-6 md:py-12 px-4">
                        <div className="w-full max-w-[95%] md:max-w-[1101px] flex flex-col bg-white rounded-[18px] md:rounded-[22.38px] overflow-hidden border-4 md:border-[6.71px] border-solid border-[#fefce7]">
                            {/* 累計評論數量 - 响应式 */}
                            <div className="min-h-[60px] md:h-[78px] w-full relative border-b-[2px] md:border-b-[2.24px] [border-bottom-style:solid] border-[#e8d080] flex items-center justify-between px-4 md:px-8 lg:px-12 py-3 md:py-0">
                                <div className="[font-family:'Noto_Sans_TC',Helvetica] font-medium text-[#929084] text-lg md:text-2xl lg:text-[32px] text-center tracking-[1.80px] md:tracking-[2.40px] lg:tracking-[3.20px] leading-tight md:leading-[35.8px]">
                                    累計評論數量
                                </div>
                                <div className="[font-family:'Noto_Sans_TC',Helvetica] font-medium text-[#33363f] text-lg md:text-2xl lg:text-[32px] text-center tracking-[1.80px] md:tracking-[2.40px] lg:tracking-[3.20px] leading-tight md:leading-[35.8px] whitespace-nowrap">
                                    {totalComments} 則
                                </div>
                            </div>

                            {/* 平均評論星等 - 响应式 */}
                            <div className="min-h-[60px] md:h-[79px] w-full relative flex items-center justify-between px-4 md:px-8 lg:px-12 py-3 md:py-0">
                                <div className="[font-family:'Noto_Sans_TC',Helvetica] font-medium text-[#929084] text-lg md:text-2xl lg:text-[32px] text-center tracking-[1.80px] md:tracking-[2.40px] lg:tracking-[3.20px] leading-tight md:leading-[35.8px]">
                                    平均評論星等
                                </div>
                                <div className="flex items-center gap-1.5 md:gap-2">
                                    <div className="[font-family:'Noto_Sans_TC',Helvetica] font-medium text-[#33363f] text-lg md:text-2xl lg:text-[32px] text-center tracking-[1.80px] md:tracking-[2.40px] lg:tracking-[3.20px] leading-tight md:leading-[35.8px] whitespace-nowrap">
                                        {averageRating.toFixed(1)}
                                    </div>
                                    <img
                                        className="w-[22px] h-[22px] md:w-[26px] md:h-[26px] lg:w-[29px] lg:h-[29px]"
                                        alt="Star"
                                        src="/img/star-3.svg"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 評論列表 - 响应式 */}
                    <div className="flex flex-col items-center justify-center pt-8 md:pt-[50px] pb-16 md:pb-[100px] px-4 relative self-stretch w-full flex-[0_0_auto] bg-[#eaeade]">
                        <div className="flex flex-col w-full max-w-[95%] md:max-w-[1096px] items-center gap-2 md:gap-2.5 px-4 md:px-8 lg:px-[60px] py-6 md:py-10 relative bg-[#7ca387] rounded-[30px] md:rounded-[41.85px] overflow-hidden border-4 md:border-[6.28px] border-solid border-[#fefce7]">
                            {loading ? (
                                <div className="text-white text-lg md:text-2xl">載入中...</div>
                            ) : error ? (
                                <div className="text-white text-lg md:text-2xl text-center px-4">{error}</div>
                            ) : comments.length > 0 ? (
                                <>
                                    {comments.map((comment, index) => (
                                        <CommentItem
                                            key={index}
                                            comment={comment}
                                            onReply={() => handleCommentSelect(comment)}
                                        />
                                    ))}
                                    {/* 底部間距 - 响应式 */}
                                    <div className="relative w-full h-12 md:h-20" />
                                </>
                            ) : (
                                <div className="text-white text-lg md:text-2xl">暫無評論資料</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}



